import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { consentApi, policyApi } from "../services/gdprApi";
import { Link } from "react-router-dom";

const CookieBanner: React.FC = () => {
  const { t } = useTranslation();
  const { userInfo, user } = useAuth();
  // State to control the visibility of the cookie banner
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [consentTypes, setConsentTypes] = useState({
    essential: true, // Always required
    analytics: false,
    marketing: false,
  });

  // Check if the user has already accepted the cookie policy (stored in localStorage or backend)
  useEffect(() => {
    const hasAcceptedCookies = localStorage.getItem("cookiesAccepted");
    if (!hasAcceptedCookies) {
      setIsVisible(true); // Show the banner if not accepted
    }
  }, []);

  // Handle the user accepting all cookies
  const handleAcceptAll = async (): Promise<void> => {
    try {
      setLoading(true);
      const email = (userInfo as { email?: string })?.email || user?.email || "guest";
      let version = "1.0";
      try {
        const versionResponse = await policyApi.getPolicyVersion("privacy");
        version = versionResponse?.version || "1.0";
      } catch {
        // Use default version on error
        version = "1.0";
      }

      // Accept cookie policy
      await policyApi.acceptPolicy({
        policyType: "cookie",
        version: version,
        email: email,
      });

      // Grant cookie consent with all types
      await consentApi.grantConsent({
        consentType: "cookies",
        version: version,
        email: email,
      });

      // Store in localStorage for UI state
      localStorage.setItem("cookiesAccepted", "true");
      localStorage.setItem("cookiePreferences", JSON.stringify(consentTypes));
      setIsVisible(false);
    } catch (error: any) {
      console.error("Error accepting cookies:", error);
      // Still hide banner and store locally on error (better UX)
      localStorage.setItem("cookiesAccepted", "true");
      setIsVisible(false);
    } finally {
      setLoading(false);
    }
  };

  // Handle the user accepting only essential cookies
  const handleAcceptEssential = async (): Promise<void> => {
    try {
      setLoading(true);
      const email = (userInfo as { email?: string })?.email || user?.email || "guest";
      let version = "1.0";
      try {
        const versionResponse = await policyApi.getPolicyVersion("privacy");
        version = versionResponse?.version || "1.0";
      } catch {
        // Use default version on error
        version = "1.0";
      }

      // Accept cookie policy with essential only
      await policyApi.acceptPolicy({
        policyType: "cookie",
        version: version,
        email: email,
      });

      // Store in localStorage for UI state (essential only)
      const essentialOnly = { essential: true, analytics: false, marketing: false };
      localStorage.setItem("cookiesAccepted", "true");
      localStorage.setItem("cookiePreferences", JSON.stringify(essentialOnly));
      setIsVisible(false);
    } catch (error: any) {
      console.error("Error accepting essential cookies:", error);
      // Still hide banner and store locally on error (better UX)
      localStorage.setItem("cookiesAccepted", "true");
      setIsVisible(false);
    } finally {
      setLoading(false);
    }
  };

  // Handle the user customizing cookie preferences
  const handleSavePreferences = async (): Promise<void> => {
    try {
      setLoading(true);
      const email = (userInfo as { email?: string })?.email || user?.email || "guest";
      let version = "1.0";
      try {
        const versionResponse = await policyApi.getPolicyVersion("privacy");
        version = versionResponse?.version || "1.0";
      } catch {
        // Use default version on error
        version = "1.0";
      }

      // Accept cookie policy
      await policyApi.acceptPolicy({
        policyType: "cookie",
        version: version,
        email: email,
      });

      // Grant cookie consent with selected types
      await consentApi.grantConsent({
        consentType: "cookies",
        version: version,
        email: email,
      });

      // Store in localStorage for UI state
      localStorage.setItem("cookiesAccepted", "true");
      localStorage.setItem("cookiePreferences", JSON.stringify(consentTypes));
      setIsVisible(false);
      setShowPreferences(false);
    } catch (error: any) {
      console.error("Error saving cookie preferences:", error);
      // Still hide banner and store locally on error (better UX)
      localStorage.setItem("cookiesAccepted", "true");
      localStorage.setItem("cookiePreferences", JSON.stringify(consentTypes));
      setIsVisible(false);
      setShowPreferences(false);
    } finally {
      setLoading(false);
    }
  };

  // Show the banner if it's visible
  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 w-full bg-gray-800 text-white p-4 z-50 shadow-lg">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex-1">
            <p className="text-sm mb-2">{t("cookieBanner.message")}</p>
            <p className="text-xs text-gray-400">
              {t("cookieBanner.description")}{" "}
              <Link to="/pages/privacy" className="text-blue-400 underline">
                {t("cookieBanner.learnMore")}
              </Link>
              {" "}{t("cookieBanner.or")}{" "}
              <button
                onClick={() => setShowPreferences(!showPreferences)}
                className="text-blue-400 underline"
              >
                {t("cookieBanner.customizePreferences")}
              </button>
            </p>

            {showPreferences && (
              <div className="mt-4 p-4 bg-gray-700 rounded">
                <p className="text-sm font-semibold mb-2">{t("cookieBanner.preferencesTitle")}</p>
                <div className="space-y-2 text-sm">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={consentTypes.essential}
                      disabled
                      className="mr-2"
                    />
                    <span>{t("cookieBanner.essentialCookies")}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={consentTypes.analytics}
                      onChange={(e) =>
                        setConsentTypes({ ...consentTypes, analytics: e.target.checked })
                      }
                      className="mr-2"
                    />
                    <span>{t("cookieBanner.analyticsCookies")}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={consentTypes.marketing}
                      onChange={(e) =>
                        setConsentTypes({ ...consentTypes, marketing: e.target.checked })
                      }
                      className="mr-2"
                    />
                    <span>{t("cookieBanner.marketingCookies")}</span>
                  </label>
                </div>
                <button
                  onClick={handleSavePreferences}
                  disabled={loading}
                  className="mt-3 bg-blue-500 text-white px-4 py-2 text-sm rounded hover:bg-blue-600 disabled:opacity-50"
                >
                  {loading ? t("cookieBanner.saving") : t("cookieBanner.savePreferences")}
                </button>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {!showPreferences && (
              <>
                <button
                  onClick={handleAcceptEssential}
                  disabled={loading}
                  className="bg-gray-600 text-white px-4 py-2 text-sm rounded hover:bg-gray-500 disabled:opacity-50"
                >
                  {loading ? t("cookieBanner.processing") : t("cookieBanner.essentialOnly")}
                </button>
                <button
                  onClick={handleAcceptAll}
                  disabled={loading}
                  className="bg-blue-500 text-white px-4 py-2 text-sm rounded hover:bg-blue-600 disabled:opacity-50"
                >
                  {loading ? "Processing..." : t("cookieBanner.accept")}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;

